/**
 * views/reporting/usePmCharts.js - win rate by account, top open bids,
 * open bid aging, margin by customer, quote-to-PO turnaround, and the Viz
 * Studio's CSV export.
 * Moved verbatim out of ReportingView.jsx; the view passes in the values
 * it reads.
 */
import { useMemo } from 'react';
import { exportCsv } from '../../utils/exportCsv';
import { trunc, safeSum } from './reportingHelpers';
import { getFilteredSets } from './chartFilters';

export function usePmCharts({ projectsThisYear }) {
  // Win Rate by Account section
  // Win Rate by Account (uses global year filter)
  const winRateByAccount = useMemo(() => {
    const custMap = {};
    projectsThisYear.forEach((p) => {
      const c = p.customer;
      if (!c) return;
      const s = (p.status || '').toLowerCase();
      if (s !== 'won' && s !== 'lost') return;
      if (!custMap[c]) custMap[c] = { won: 0, lost: 0 };
      if (s === 'won') custMap[c].won++;
      else custMap[c].lost++;
    });
    return Object.entries(custMap)
      .filter(([, v]) => v.won + v.lost >= 1)
      .map(([name, v]) => ({
        name: trunc(name.replace('FANUC America - ', 'FANUC ')),
        won: v.won,
        lost: v.lost,
        winRate: parseFloat(((v.won / (v.won + v.lost)) * 100).toFixed(1)),
      }))
      .sort((a, b) => b.winRate - a.winRate);
  }, [projectsThisYear]);

  // Top Open Bids section
  // Top Open Bids (uses global year filter)
  const topBidsSets = useMemo(() => getFilteredSets(projectsThisYear), [projectsThisYear]);
  const topBids = useMemo(
    () => [...topBidsSets.activeBids].sort((a, b) => parseFloat(b.quotedPrice || 0) - parseFloat(a.quotedPrice || 0)).slice(0, 15),
    [topBidsSets.activeBids]
  );
  const topBidsTotal = safeSum(topBidsSets.activeBids);

  // ── PM-useful charts ────────────────────────────────────────
  // Open Bid Aging: days since quote_date for each open bid in the selected year.
  const today = useMemo(() => new Date(), []);
  const openBidAging = useMemo(() => {
    return topBidsSets.activeBids
      .map((p) => {
        const d = p.quoteDate || p.quote_date;
        if (!d) return null;
        const dt = new Date(d);
        if (isNaN(dt)) return null;
        const days = Math.max(0, Math.round((today - dt) / (1000 * 60 * 60 * 24)));
        return {
          id: p.id,
          name: trunc(p.name || p.description || `${p.customer || ''}: ${p.quoteLetter || ''}`, 30),
          days,
          customer: p.customer,
          value: parseFloat(p.quotedPrice || 0) || 0,
          quoteDate: d,
          quoteLetter: p.quoteLetter || p.quote_letter,
          status: p.status,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.days - a.days)
      .slice(0, 12);
  }, [topBidsSets.activeBids, today]);

  const agingBuckets = useMemo(() => {
    const buckets = { '<30d': 0, '30–60d': 0, '60–90d': 0, '>90d': 0 };
    topBidsSets.activeBids.forEach((p) => {
      const d = p.quoteDate || p.quote_date;
      if (!d) return;
      const dt = new Date(d);
      if (isNaN(dt)) return;
      const days = Math.max(0, Math.round((today - dt) / 86400000));
      if (days < 30) buckets['<30d']++;
      else if (days < 60) buckets['30–60d']++;
      else if (days < 90) buckets['60–90d']++;
      else buckets['>90d']++;
    });
    return Object.entries(buckets).map(([name, value]) => ({ name, value }));
  }, [topBidsSets.activeBids, today]);

  // Margin by Customer — for won deals with recorded labor cost. Top 8.
  const marginByCustomer = useMemo(() => {
    const m = {};
    projectsThisYear.forEach((p) => {
      if ((p.status || '').toLowerCase() !== 'won') return;
      // totalQuotedCogs comes from the server and honors quoted_cogs_override
      // (a fixed COGS figure kept on the row) - see routes/projects.js.
      const cogs = parseFloat(p.totalQuotedCogs ?? p.total_quoted_cogs ?? 0);
      if (!cogs) return;
      const price = parseFloat(p.quotedPrice || p.quoted_price || 0);
      if (!price || price > 500e6) return;
      const c = p.customer;
      if (!c) return;
      if (!m[c]) m[c] = { price: 0, cogs: 0, count: 0 };
      m[c].price += price;
      m[c].cogs += cogs;
      m[c].count += 1;
    });
    return Object.entries(m)
      .filter(([, v]) => v.price > 0 && v.count >= 1)
      .map(([name, v]) => ({
        name: trunc(name.replace('FANUC America - ', 'FANUC '), 22),
        margin: parseFloat((((v.price - v.cogs) / v.price) * 100).toFixed(1)),
        n: v.count,
        value: parseFloat((((v.price - v.cogs) / v.price) * 100).toFixed(1)),
      }))
      .sort((a, b) => b.margin - a.margin)
      .slice(0, 8);
  }, [projectsThisYear]);

  // Quote → PO turnaround for won deals. Avg + median.
  const quoteToPoStats = useMemo(() => {
    const days = [];
    projectsThisYear.forEach((p) => {
      if ((p.status || '').toLowerCase() !== 'won') return;
      const q = p.quoteDate || p.quote_date;
      const po = p.poDateIssued || p.po_date_issued;
      if (!q || !po) return;
      const qd = new Date(q);
      const pd = new Date(po);
      if (isNaN(qd) || isNaN(pd)) return;
      const d = Math.round((pd - qd) / 86400000);
      if (d >= 0 && d < 730) days.push(d);
    });
    if (!days.length) return null;
    const sum = days.reduce((s, d) => s + d, 0);
    const avg = Math.round(sum / days.length);
    const sorted = [...days].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    return { count: days.length, avg, median, fast: days.filter((d) => d <= 14).length, slow: days.filter((d) => d > 60).length };
  }, [projectsThisYear]);

  const handleVizExport = (data, label) => {
    exportCsv(
      `${label.toLowerCase().replace(/\s+/g, '_')}.csv`,
      data.map((d) => ({ Name: d.name, Value: d.value }))
    );
  };
  return {
    handleVizExport,
    openBidAging,
    agingBuckets,
    winRateByAccount,
    marginByCustomer,
    quoteToPoStats,
    topBidsSets,
    topBids,
    topBidsTotal,
  };
}
